import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';

import { BusinessDocumentWorkspaceModel } from '../../../core/services/business-document.service';
import {
  BusinessDocumentChatReference,
  ChatConversation,
  ChatMessage,
  ChatService,
  ChatUser,
  PendingChatAttachment,
} from '../../../core/services/chat.service';

type DrawerMode = 'list' | 'thread' | 'new' | 'share';

@Component({
  selector: 'app-business-document-chat-drawer',
  standalone: false,
  templateUrl: './business-document-chat-drawer.html',
  styleUrl: './business-document-chat-drawer.scss',
})
export class BusinessDocumentChatDrawer implements OnChanges {
  @Input({ required: true }) workspace!: BusinessDocumentWorkspaceModel;
  @Input() businessDocumentRoute = '';

  mode: DrawerMode = 'list';
  conversations: BusinessDocumentChatReference[] = [];
  availableConversations: ChatConversation[] = [];
  conversation: ChatConversation | null = null;
  messages: ChatMessage[] = [];
  userResults: ChatUser[] = [];
  selectedUserIds = new Set<string>();
  search = '';
  groupTitle = '';
  message = '';
  attachments: PendingChatAttachment[] = [];
  loading = false;
  creating = false;
  sending = false;
  uploading = false;
  errorMessage = '';

  constructor(
    private readonly chat: ChatService,
    private readonly snackBar: MatSnackBar,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['workspace']?.currentValue) {
      this.groupTitle = `${this.workspace.document.document_number} · ${this.workspace.document.display_name}`;
      void this.loadConversations();
    }
  }

  get documentRoute(): string {
    return this.conversation?.business_document_route
      || this.businessDocumentRoute
      || globalThis.location?.pathname
      || '/';
  }

  showList(): void {
    this.mode = 'list';
    this.conversation = null;
    this.messages = [];
    this.errorMessage = '';
  }

  async openConversation(reference: BusinessDocumentChatReference): Promise<void> {
    if (!reference.can_read_messages) return;
    this.loading = true;
    this.errorMessage = '';
    try {
      this.conversation = await this.chat.getConversation(reference.id);
      await this.loadMessages();
      this.mode = 'thread';
    } catch (error: any) {
      this.errorMessage = error?.error?.message ?? 'The conversation could not be opened.';
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  async startNew(): Promise<void> {
    this.mode = 'new';
    this.selectedUserIds = new Set<string>();
    await this.searchUsers('');
  }

  async chooseExisting(): Promise<void> {
    this.loading = true;
    try {
      const page = await this.chat.listConversations(1, 100);
      const attached = new Set(this.conversations.map((item) => item.id));
      this.availableConversations = page.items.filter((item) =>
        !attached.has(item.id) && (!item.business_document_id || item.business_document_id === this.workspace.document.id),
      );
      this.mode = 'share';
    } catch (error: any) {
      this.errorMessage = error?.error?.message ?? 'Your conversations could not be loaded.';
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  async shareExisting(conversation: ChatConversation): Promise<void> {
    if (this.creating) return;
    this.creating = true;
    try {
      const attached = await this.chat.attachBusinessDocument(
        conversation.id,
        this.workspace.document.id,
        this.documentRoute,
      );
      await this.chat.sendMessage(attached.id, this.sharedDocumentMessage());
      await this.loadConversations();
      const reference = this.conversations.find((item) => item.id === attached.id);
      if (reference) await this.openConversation(reference);
      this.snackBar.open('The document was shared to the chat.', 'Close', { duration: 3000 });
    } catch (error: any) {
      this.errorMessage = error?.error?.message ?? 'The document could not be shared to this chat.';
    } finally {
      this.creating = false;
      this.cdr.detectChanges();
    }
  }

  async searchUsers(value: string): Promise<void> {
    this.search = value;
    try {
      this.userResults = await this.chat.searchUsers(value.trim(), 25);
    } catch {
      this.userResults = [];
    }
    this.cdr.detectChanges();
  }

  toggleUser(id: string): void {
    const next = new Set(this.selectedUserIds);
    next.has(id) ? next.delete(id) : next.add(id);
    this.selectedUserIds = next;
  }

  async create(): Promise<void> {
    if (!this.selectedUserIds.size || !this.groupTitle.trim() || this.creating) return;
    this.creating = true;
    this.errorMessage = '';
    try {
      const created = await this.chat.createBusinessDocumentGroupConversation(
        this.groupTitle.trim(),
        [...this.selectedUserIds],
        this.workspace.document.id,
        this.documentRoute,
      );
      await this.chat.sendMessage(created.id, this.sharedDocumentMessage());
      await this.loadConversations();
      const reference = this.conversations.find((item) => item.id === created.id);
      if (reference) await this.openConversation(reference);
      this.snackBar.open('Document group chat created.', 'Close', { duration: 3000 });
    } catch (error: any) {
      this.errorMessage = error?.error?.message ?? 'The document group chat could not be created.';
    } finally {
      this.creating = false;
      this.cdr.detectChanges();
    }
  }

  async send(): Promise<void> {
    if (!this.conversation || this.sending || (!this.message.trim() && !this.attachments.length)) return;
    this.sending = true;
    try {
      const sent = await this.chat.sendMessage(this.conversation.id, this.message.trim(), this.attachments);
      this.messages = [...this.messages, sent];
      this.message = '';
      this.attachments = [];
    } catch (error: any) {
      this.snackBar.open(error?.error?.message ?? 'The message could not be sent.', 'Close', { duration: 5000 });
    } finally {
      this.sending = false;
      this.cdr.detectChanges();
    }
  }

  async attach(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || this.uploading) return;
    this.uploading = true;
    try {
      this.attachments = [...this.attachments, await this.chat.uploadAttachment(file)];
    } catch (error: any) {
      this.snackBar.open(error?.message ?? 'The attachment could not be uploaded.', 'Close', { duration: 5000 });
    } finally {
      this.uploading = false;
      this.cdr.detectChanges();
    }
  }

  removeAttachment(objectKey: string): void {
    this.attachments = this.attachments.filter((item) => item.object_key !== objectKey);
  }

  async openAttachment(id: string): Promise<void> {
    try {
      globalThis.open(await this.chat.attachmentDownloadUrl(id), '_blank', 'noopener,noreferrer');
    } catch {
      this.snackBar.open('The attachment could not be opened.', 'Close', { duration: 4000 });
    }
  }

  private async loadConversations(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';
    try {
      this.conversations = await this.chat.listBusinessDocumentConversations(this.workspace.document.id);
      this.mode = 'list';
    } catch (error: any) {
      this.errorMessage = error?.error?.message ?? 'The document chats could not be loaded.';
      this.conversations = [];
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  private async loadMessages(): Promise<void> {
    if (!this.conversation) return;
    this.messages = (await this.chat.listMessages(this.conversation.id, 1, 100)).items;
    await this.chat.markRead(this.conversation.id);
  }

  private sharedDocumentMessage(): string {
    return `Shared ${this.workspace.document.document_number}: ${this.workspace.document.display_name}\n${this.documentRoute}`;
  }
}
